// El lienzo interactivo. Mide el área disponible, calcula el tamaño del stage con
// "contain" (respetando el ratio), y separa DOS capas:
//   1) contenido (clipeado al lienzo, pointer-events off) → las imágenes/textos/formas.
//   2) interacción (sin clip) → hit-boxes por capa (selección + drag) y el marco de
//      selección con manijas de resize. Sin clip para que las manijas de una capa a
//      borde completo (la imagen base) no queden cortadas.
// Todo en % del lienzo; el drag/resize convierte px→% con el tamaño del stage.

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { Layer, Scene, TextLayer } from '../scene/types';
import { LayerView } from './LayerView';
import { FloatingToolbar } from './FloatingToolbar';
import { fontStack } from '../lib/fonts';
import { useIsDesktop } from '../lib/useIsDesktop';
import {
  SelectionFrame,
  type SelectionFramePhase,
  type SelectionHandle,
} from '../../../components/SelectionFrame';

const MIN_PCT = 3; // tamaño mínimo de una capa (% del lienzo)

function fitContain(areaW: number, areaH: number, ratio: number) {
  if (!areaW || !areaH || !ratio) return { w: 0, h: 0 };
  const areaRatio = areaW / areaH;
  if (ratio >= areaRatio) return { w: areaW, h: areaW / ratio };
  return { w: areaH * ratio, h: areaH };
}

function geom(l: Layer): CSSProperties {
  return {
    position: 'absolute',
    left: `${l.xPct}%`,
    top: `${l.yPct}%`,
    width: `${l.wPct}%`,
    height: `${l.hPct}%`,
    transform: `translate(-50%, -50%) rotate(${l.rotation ?? 0}deg)`,
  };
}

/**
 * "Esta capa se está procesando": un brillo que la barre + un halo que late.
 *
 * Va SOBRE la capa y clipeado a su forma (hereda el redondeo de la imagen), así que
 * se lee como que la IA está trabajando sobre ESA foto y no sobre el lienzo entero.
 * Reusa las keyframes de la expansión mágica, que ya traen su `prefers-reduced-motion`.
 */
function CapaProcesando({ layer }: { layer: Layer }) {
  const radius = layer.type === 'image' ? `${layer.radiusPct}%` : '0';
  return (
    <div
      aria-hidden
      className="absolute inset-0 overflow-hidden pointer-events-none"
      style={{ borderRadius: radius }}
    >
      <div
        className="absolute inset-0 expansion-latido"
        style={{ boxShadow: 'inset 0 0 0 2px rgb(var(--brand-primary-rgb) / 0.55)', borderRadius: radius }}
      />
      <div
        className="absolute inset-y-0 -left-1/3 w-1/3 expansion-barrido"
        style={{
          background:
            'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.55) 50%, rgba(255,255,255,0) 100%)',
        }}
      />
    </div>
  );
}

function TextEditor({
  layer,
  stageH,
  onInput,
  onDone,
}: {
  layer: TextLayer;
  stageH: number;
  onInput: (text: string) => void;
  onDone: (text: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerText = layer.text;
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    // solo al montar (entrar a editar): setea texto, foco y cursor al final.
  }, []);
  const justify =
    layer.align === 'left' ? 'flex-start' : layer.align === 'right' ? 'flex-end' : 'center';
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: justify }}>
      <div
        contentEditable
        suppressContentEditableWarning
        ref={ref}
        onInput={(e) => onInput((e.currentTarget as HTMLDivElement).innerText)}
        onBlur={(e) => onDone((e.currentTarget as HTMLDivElement).innerText)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Escape') (e.currentTarget as HTMLDivElement).blur();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          outline: 'none',
          pointerEvents: 'auto',
          fontFamily: fontStack(layer.fontFamily),
          fontSize: (layer.fontSizePct / 100) * stageH,
          fontWeight: layer.fontWeight,
          fontStyle: layer.fontStyle,
          color: layer.color,
          textAlign: layer.align,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          lineHeight: 1.15,
          caretColor: layer.color,
        }}
      />
    </div>
  );
}

interface Props {
  scene: Scene;
  select: (id: string | null) => void;
  checkpoint: () => void;
  updateLayer: (id: string, patch: Partial<Layer>) => void;
  onTextCommit?: (text: string) => void;
  onEditingChange?: (editing: boolean) => void;
  // Acciones del nav flotante (aparece sobre la capa seleccionada).
  onDuplicate?: () => void;
  onRemove?: () => void;
  onOpenLayers?: () => void;
  /** Capa sobre la que está corriendo una herramienta de IA: se le pinta el barrido. */
  busyLayerId?: string | null;
  /** Marca de agua: escena de 2 capas fijas, sin duplicar/capas/borrar. */
  hideFloatingNav?: boolean;
}

export function CanvasStage({
  scene,
  select,
  checkpoint,
  updateLayer,
  onTextCommit,
  onEditingChange,
  onDuplicate,
  onRemove,
  onOpenLayers,
  busyLayerId,
  hideFloatingNav,
}: Props) {
  const areaRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [area, setArea] = useState({ w: 0, h: 0 });
  const isDesktop = useIsDesktop();
  const [editingId, setEditingId] = useState<string | null>(null);
  // Checkpoint UNA vez al empezar a editar un texto (en el primer cambio), para
  // que el undo restaure el texto previo — no una acción anterior. Igual patrón
  // que el drag/resize.
  const editCheckpointedRef = useRef(false);

  // Avisar al shell si hay edición inline activa → deshabilita undo/redo mientras
  // se edita (si no, revertir el estado dejaría el contentEditable desincronizado;
  // el Ctrl+Z nativo del contentEditable sigue deshaciendo el tipeo).
  useEffect(() => {
    onEditingChange?.(editingId !== null);
  }, [editingId, onEditingChange]);

  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    const update = () => setArea({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Margen real alrededor del lienzo (que no toque los bordes ni salte).
  const PAD = 28;
  const { w: stageW, h: stageH } = fitContain(
    Math.max(0, area.w - PAD * 2),
    Math.max(0, area.h - PAD * 2),
    scene.ratio,
  );

  // ── Move (arrastrar una capa entera) ──
  // Sin setPointerCapture (rompía el dblclick NATIVO del texto): usamos listeners
  // de window durante el drag → anda aunque el puntero salga del lienzo, y el
  // doble-click para editar texto vuelve a funcionar.
  const beginMove = (e: React.PointerEvent, layer: Layer) => {
    e.stopPropagation();
    select(layer.id);
    if (layer.locked) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startXPct = layer.xPct;
    const startYPct = layer.yPct;
    let checkpointed = false;
    const onMove = (ev: PointerEvent) => {
      if (!stageW || !stageH) return;
      if (!checkpointed) {
        checkpoint();
        checkpointed = true;
      }
      const dxPct = ((ev.clientX - startX) / stageW) * 100;
      const dyPct = ((ev.clientY - startY) / stageH) * 100;
      updateLayer(layer.id, { xPct: startXPct + dxPct, yPct: startYPct + dyPct });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // ── Resize (via SelectionFrame, que da dx/dy en px + phase) ──
  const resizeRef = useRef<{
    box: { xPct: number; yPct: number; wPct: number; hPct: number };
    done: boolean;
  } | null>(null);

  const handleResize =
    (layer: Layer) =>
    (handle: SelectionHandle, dx: number, dy: number, phase: SelectionFramePhase) => {
      if (phase === 'start') {
        resizeRef.current = {
          box: { xPct: layer.xPct, yPct: layer.yPct, wPct: layer.wPct, hPct: layer.hPct },
          done: false,
        };
        return;
      }
      const r = resizeRef.current;
      if (!r || !stageW || !stageH) return;
      if (!r.done) {
        checkpoint();
        r.done = true;
      }
      // La capa se dibuja con `rotate(θ)` alrededor de su centro. El puntero da
      // dx/dy en px de PANTALLA; para redimensionar bien hay que (a) llevarlos al
      // marco LOCAL de la capa (rotar por -θ) y (b) correr el centro para que el
      // lado/esquina OPUESTA quede fija en pantalla. Sin esto, con la capa rotada
      // el handle no seguía al puntero. Todo en px; recién al final se pasa a %.
      const theta = ((layer.rotation ?? 0) * Math.PI) / 180;
      const cos = Math.cos(theta);
      const sin = Math.sin(theta);
      // Delta de pantalla → delta local (R(-θ)·d).
      const localDx = dx * cos + dy * sin;
      const localDy = -dx * sin + dy * cos;

      const wPx = (r.box.wPct / 100) * stageW;
      const hPx = (r.box.hPct / 100) * stageH;
      const minWpx = (MIN_PCT / 100) * stageW;
      const minHpx = (MIN_PCT / 100) * stageH;
      // Lado(s) que se mueven: +1 = e/s, -1 = w/n, 0 = eje sin cambio.
      const sx = handle.includes('e') ? 1 : handle.includes('w') ? -1 : 0;
      const sy = handle.includes('s') ? 1 : handle.includes('n') ? -1 : 0;

      // Desde una ESQUINA, imágenes y formas mantienen la proporción (escala
      // uniforme anclada en la esquina opuesta → no se deforman). El texto y los
      // lados (n/s/e/w) siguen libres.
      const lockAspect =
        handle.length === 2 && (layer.type === 'image' || layer.type === 'shape');

      let newWpx: number;
      let newHpx: number;
      if (lockAspect) {
        const kx = (wPx + sx * localDx) / wPx;
        const ky = (hPx + sy * localDy) / hPx;
        // Driver = el eje LOCAL que más se movió; el otro sigue proporcional.
        let k = Math.abs(localDx) >= Math.abs(localDy) ? kx : ky;
        // Ningún lado por debajo del mínimo (mantiene la proporción).
        k = Math.max(minWpx / wPx, minHpx / hPx, k);
        newWpx = wPx * k;
        newHpx = hPx * k;
      } else {
        newWpx = sx !== 0 ? Math.max(minWpx, wPx + sx * localDx) : wPx;
        newHpx = sy !== 0 ? Math.max(minHpx, hPx + sy * localDy) : hPx;
      }

      // El centro se corre ΔC = R(θ)·(sx·ΔW/2, sy·ΔH/2) para dejar el lado/esquina
      // opuesta fijo en pantalla (con θ=0 esto es el midpoint de siempre).
      const ddx = (sx * (newWpx - wPx)) / 2;
      const ddy = (sy * (newHpx - hPx)) / 2;
      const cxPx = (r.box.xPct / 100) * stageW + (ddx * cos - ddy * sin);
      const cyPx = (r.box.yPct / 100) * stageH + (ddx * sin + ddy * cos);

      updateLayer(layer.id, {
        xPct: (cxPx / stageW) * 100,
        yPct: (cyPx / stageH) * 100,
        wPct: (newWpx / stageW) * 100,
        hPct: (newHpx / stageH) * 100,
      });
      if (phase === 'end') resizeRef.current = null;
    };

  // Rotación: la zona alrededor de las esquinas reporta la posición del puntero.
  // Se rota de forma RELATIVA (delta desde el ángulo donde se agarró) para que no
  // pegue un salto al empezar, sin importar desde qué esquina se arrastre.
  const rotRef = useRef<{ startAngle: number; startRotation: number; checkpointed: boolean } | null>(
    null,
  );
  const handleRotate =
    (layer: Layer) =>
    (clientX: number, clientY: number, phase: SelectionFramePhase) => {
      const el = stageRef.current;
      if (!el || !stageW || !stageH) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + (layer.xPct / 100) * stageW;
      const cy = rect.top + (layer.yPct / 100) * stageH;
      const angle = (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI;
      if (phase === 'start') {
        rotRef.current = { startAngle: angle, startRotation: layer.rotation ?? 0, checkpointed: false };
        return;
      }
      const r = rotRef.current;
      if (!r) return;
      if (!r.checkpointed) {
        checkpoint();
        r.checkpointed = true;
      }
      let deg = r.startRotation + (angle - r.startAngle);
      deg = Math.round(((deg % 360) + 360) % 360);
      updateLayer(layer.id, { rotation: deg });
      if (phase === 'end') rotRef.current = null;
    };

  // Nav flotante sobre la capa seleccionada (oculto al editar texto). Se posiciona
  // en coordenadas del ÁREA visible (no del stage) y se CLAMPEA al área, para que
  // NUNCA quede cortado — clave cuando la capa es la imagen base que llena el
  // lienzo: ahí no cabe ni arriba ni abajo, y queda anclado al borde inferior.
  const NAV_W = 132;
  const NAV_H = 44;
  const NAV_M = 12;
  const selLayer = scene.layers.find((l) => l.id === scene.selectedId) ?? null;
  const showNav =
    !hideFloatingNav && !!selLayer && editingId !== scene.selectedId && stageW > 0 && stageH > 0;
  let navLeft = 0;
  let navTop = 0;
  if (showNav && selLayer) {
    const stageTop = (area.h - stageH) / 2;
    const stageLeft = (area.w - stageW) / 2;
    const elemTop = stageTop + ((selLayer.yPct - selLayer.hPct / 2) / 100) * stageH;
    const elemBottom = stageTop + ((selLayer.yPct + selLayer.hPct / 2) / 100) * stageH;
    const aboveTop = elemTop - NAV_H - 10;
    const belowTop = elemBottom + 10;
    if (aboveTop >= NAV_M) navTop = aboveTop;
    else if (belowTop + NAV_H <= area.h - NAV_M) navTop = belowTop;
    else navTop = area.h - NAV_H - NAV_M; // capa grande → anclar al fondo visible
    navTop = Math.max(NAV_M, Math.min(navTop, area.h - NAV_H - NAV_M));
    navLeft = stageLeft + (selLayer.xPct / 100) * stageW;
    navLeft = Math.max(NAV_W / 2 + 8, Math.min(navLeft, area.w - NAV_W / 2 - 8));
  }
  // El tooltip apunta hacia el centro (más espacio): abajo si el nav está arriba.
  const tooltipSide: 'top' | 'bottom' = navTop < area.h / 2 ? 'bottom' : 'top';

  return (
    <div
      ref={areaRef}
      className="relative flex-1 min-h-0 w-full flex items-center justify-center overflow-visible"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) select(null);
      }}
    >
      {stageW > 0 && (
        <div
          ref={stageRef}
          className="relative rounded-md shadow-md"
          style={{ width: stageW, height: stageH, touchAction: 'none' }}
        >
          {/* 1) Contenido, clipeado al lienzo */}
          <div
            className="absolute inset-0 overflow-hidden rounded-md pointer-events-none"
            style={{ background: scene.bg }}
          >
            {scene.layers.map((l) =>
              editingId === l.id ? null : (
                <div key={l.id} style={geom(l)}>
                  <LayerView layer={l} stageW={stageW} stageH={stageH} />
                  {l.id === busyLayerId && <CapaProcesando layer={l} />}
                </div>
              ),
            )}
          </div>

          {/* 2) Interacción (hit-boxes + marco de selección), sin clip */}
          <div
            className="absolute inset-0"
            onPointerDown={(e) => {
              if (e.target === e.currentTarget) select(null);
            }}
          >
            {scene.layers.map((l) => {
              // Sin hit-box para capas bloqueadas: el clic pasa de largo y
              // deselecciona (la base de la marca de agua no es interactiva).
              if (l.locked) return null;
              const selected = l.id === scene.selectedId;
              const handles: SelectionHandle[] =
                l.type === 'text'
                  ? ['nw', 'ne', 'se', 'sw', 'e', 'w']
                  : ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
              if (editingId === l.id && l.type === 'text') {
                return (
                  <div key={l.id} style={geom(l)}>
                    <TextEditor
                      layer={l}
                      stageH={stageH}
                      onInput={(text) => {
                        if (!editCheckpointedRef.current) {
                          checkpoint();
                          editCheckpointedRef.current = true;
                        }
                        updateLayer(l.id, { text });
                      }}
                      onDone={(text) => {
                        onTextCommit?.(text);
                        setEditingId(null);
                      }}
                    />
                  </div>
                );
              }
              return (
                <div
                  key={l.id}
                  data-eg-hit={l.id}
                  style={{ ...geom(l), cursor: 'move', touchAction: 'none' }}
                  onPointerDown={(e) => beginMove(e, l)}
                  onDoubleClick={(e) => {
                    if (l.type === 'text') {
                      e.stopPropagation();
                      select(l.id);
                      editCheckpointedRef.current = false;
                      setEditingId(l.id);
                    }
                  }}
                >
                  {selected && (
                    <SelectionFrame
                      color="var(--brand-primary)"
                      handles={handles}
                      onResize={handleResize(l)}
                      onRotate={handleRotate(l)}
                      mobile={!isDesktop}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showNav && (
        <div
          className="absolute z-20"
          style={{ left: navLeft, top: navTop, transform: 'translateX(-50%)' }}
        >
          <FloatingToolbar
            onDuplicate={() => onDuplicate?.()}
            onOpenLayers={() => onOpenLayers?.()}
            onRemove={() => onRemove?.()}
            tooltipSide={tooltipSide}
          />
        </div>
      )}
    </div>
  );
}
